import { createContext, useContext, ReactNode } from "react"
import { GivingOrganization } from "../types"
import { useQuery } from "@tanstack/react-query"
import { fetchGivingOrganization } from "../queries"
import { AlertMessage, Loading } from "source/shared/components"

type GivingOrganizationContextType = {
  givingOrganization: GivingOrganization
}

const GivingOrganizationContext = createContext<
  GivingOrganizationContextType | undefined
>(undefined)

export const GivingOrganizationProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const {
    data: givingOrganization,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["GivingOrganization"],
    queryFn: () => fetchGivingOrganization(),
  })

  if (isLoading) {
    return <Loading message="Loading" />
  }

  if (isError || !givingOrganization) {
    return (
      <AlertMessage type="error" icon="general#exclamation-triangle">
        Something went wrong. Please try again.
      </AlertMessage>
    )
  }

  return (
    givingOrganization && (
      <GivingOrganizationContext.Provider
        value={{
          givingOrganization,
        }}
      >
        {children}
      </GivingOrganizationContext.Provider>
    )
  )
}

export const useGivingOrganizationContext = () => {
  const context = useContext(GivingOrganizationContext)
  if (!context) {
    throw new Error(
      "useGivingOrganizationContext must be used within a GivingOrganizationProvider",
    )
  }
  return context
}
